<script setup lang="ts">
import MList from "../../MList";
import MItemLabel from "../../MItemLabel";
import MItemSection from "../../MItemSection";
import { ref, computed, onMounted } from "vue";
import MMenu from "../../MMenu";
import MInput from "../MInput";
import MLayoutStack from "../../MLayoutStack";
import MModal from "../../MModal";
import MButton from "../../MButton";

const props = defineProps<{
  modelValue?: string;
  variableOptions: Array<{ label: string; value: string }>;
  label?: string;
  allowCustomVariables: boolean;
  required: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "customVariableCreated", value: { name: string; value: string; defaultText: string }): void;
}>();

const textAreaEl = ref<HTMLElement | null>(null);
const caretPosition = ref<number>(0);

const showCustomVariableModal = ref<boolean>(false);
const customVariable_name = ref<string>("");
const customVariable_defaultText = ref<string>("");
const customVariables = ref<{ name: string; value: string; defaultText: string }[]>([]);

const showMenu = computed(() => {
  return getLastUnclosedDoubleBraceContent(props.modelValue) !== null;
});

const filteredActions = computed(() => {
  const openVariable = getLastUnclosedDoubleBraceContent(props.modelValue).substring(2);
  // This ensures when trying to add a variable in the middle of the text we only use the text
  // entered up to the first space.
  // i.e. `My name is {{pat . Nice to meet you` only searches `{{pat` NOT `{{pat . Nice to meet you`
  const searchableText = openVariable.match(/^([a-zA-Z0-9_]*)/)[0];

  const filteredList = props.variableOptions.filter((v) => {
    return v.value.includes(searchableText);
  });
  if (props.allowCustomVariables) {
    filteredList.push({ label: "Add custom variable...", value: "custom" });
  }

  return filteredList;
});

onMounted(() => {
  const el = document.getElementById("bodyContent") as HTMLTextAreaElement;
  if (!el) return;

  el.addEventListener("keyup", (e: KeyboardEvent) => {
    const target = e.target as HTMLTextAreaElement;
    caretPosition.value = target.selectionStart;
  });
});

function getLastUnclosedDoubleBraceContent(text: string): string | null {
  if (!text) return null;
  const openRe = /\{\{/g;
  const closeRe = /\}\}/g;
  let match: RegExpExecArray | null;
  let stack: number[] = [];

  // Track all opening positions
  while ((match = openRe.exec(text)) !== null) {
    stack.push(match.index);
  }

  // For each closing `}}`, pop an opening `{{`
  while ((match = closeRe.exec(text)) !== null) {
    let keyToRemove: number | null = null;
    if (stack.length > 0) {
      for (const stackKey in stack) {
        const openBracePos = stack[stackKey];
        if (match.index > openBracePos) {
          keyToRemove = Number(stackKey);
        }
        if (openBracePos > match.index) {
          break;
        }
      }
      if (keyToRemove != null) {
        stack[keyToRemove] = NaN;
      }
    }
  }

  stack = stack.filter((k) => !isNaN(k));

  // If anything remains in stack, it's an unmatched `{{`
  if (stack.length > 0) {
    const lastOpenIndex = stack[stack.length - 1];
    return text.slice(lastOpenIndex);
  }
  return null;
}

function selectVariable(variable) {
  if (variable.value === "custom") {
    if (props.allowCustomVariables) {
      showCustomVariableModal.value = true;
    }
    return;
  }
  insertVariableIntoBody(variable.value);
}

function insertVariableIntoBody(variable) {
  const firstPart = props.modelValue.substring(0, caretPosition.value + 1);
  const secondPart = props.modelValue.substring(caretPosition.value + 1);

  // This will tidy any text entered that filters the list
  // e.g. `Hi {{pat` => `Hi {{patient_name}}` NOT `Hi {{patpatient_name}}`
  const trimmedFirstSection = firstPart.replace(/({{[a-zA-Z0-9_]*[ \n]?)$/, "");
  const newValue = (trimmedFirstSection + `{{${variable}}} ` + secondPart).trim();

  emit("update:modelValue", newValue);

  focusOnContent();
}

function focusOnContent() {
  const el = textAreaEl.value as HTMLTextAreaElement | null;
  if (el) el.focus();
}

function isCustomVariableValid() {
  return validateCustomVariable() !== null || "This custom variable name is already in use.";
}

function validateCustomVariable() {
  // remove all symbols, lowercase, replace spaces with underscores
  // e.g. "How are you?" -> "how_are_you"
  const varName = customVariable_name.value
    .toLowerCase()
    .trim()
    .replaceAll(" ", "_")
    .replace(/\W/g, "");

  const variableExists = customVariables.value.some((v) => v.value === varName);
  return variableExists ? null : varName;
}

function addCustomVariable() {
  const variable = validateCustomVariable();
  // do not add as the variable already exists
  if (variable === null) return;

  const newVariable = {
    name: customVariable_name.value,
    value: variable,
    defaultText: customVariable_defaultText.value,
  };

  customVariables.value.push(newVariable);
  insertVariableIntoBody(variable + "|nl2br");

  // reset and close modal
  customVariable_name.value = "";
  customVariable_defaultText.value = "";
  showCustomVariableModal.value = false;

  emit("customVariableCreated", newVariable);
}
</script>

<template>
  <m-input
    id="bodyContent"
    ref="textAreaEl"
    :label="label || 'Body content'"
    type="textarea"
    :model-value="modelValue"
    :required="required"
    @update:model-value="(val) => emit('update:modelValue', val)"
  />
  <div id="canvas-text-area">
    <m-menu
      v-if="showMenu"
      no-refocus
      no-focus
      :target="textAreaEl"
      :model-value="true"
      :scroll-target="false"
      :max-height="300"
      fit
      :style="{ width: 250 + 'px' }"
      role="listbox"
    >
      <m-list v-if="filteredActions.length > 0" ref="list" class="scroll-area" dense tabindex="0">
        <q-item
          v-for="(variable, i) in filteredActions"
          :id="`${variable}-${i}`"
          :key="`${variable}-${i}`"
          ref="refActionMenuItems"
          v-close-popup
          clickable
          tabindex="-1"
          role="option"
          @click="() => selectVariable(variable)"
        >
          <m-item-section>
            <m-item-label>
              {{ variable.label }}
            </m-item-label>
          </m-item-section>
        </q-item>
      </m-list>
      <m-list v-else-if="props.allowCustomVariables">
        <q-item
          ref="refActionMenuItems"
          class="label empty-text"
          clickable
          focused
          @click="() => customVariable()"
        >
          <m-item-section>
            <m-item-label class="custom-variable-text">Add custom variable...</m-item-label>
          </m-item-section>
        </q-item>
      </m-list>
    </m-menu>
  </div>
  <m-modal
    v-if="props.allowCustomVariables"
    v-model="showCustomVariableModal"
    title="Add Custom Field"
    type="small"
  >
    <m-layout-stack>
      <m-input
        v-model="customVariable_name"
        label="Name"
        required
        :rules="[isCustomVariableValid]"
      />
      <m-input v-model="customVariable_defaultText" label="Default text" type="textarea" />
      <m-layout-stack horizontal end>
        <m-button
          label="Cancel"
          color="secondary"
          @click="
            () => {
              customVariable_name.value = null;
              customVariable_defaultText.value = null;
              showCustomVariableModal = false;
            }
          "
        />
        <m-button label="Add" :disable="!customVariable_name" @click="addCustomVariable" />
      </m-layout-stack>
    </m-layout-stack>
  </m-modal>
</template>
